import { ColumnSettings } from 'src/app/models/column-settings';
import { GridColumnData } from 'src/app/features/grid/classes/grid-column-data.class';
import { ColumnType, ColumnDataType } from 'src/app/features/grid/classes/column-types';

/**
 * Determines the best format to use based on ColumnSettings properties
 * @param columnSettings The source ColumnSettings object
 * @returns Object with format and pipeFormat for GridColumnData
 */
function resolveColumnFormat(columnSettings: ColumnSettings): { format: string; pipeFormat: string } {
  // If usePipeFormat is true and pipeFormat exists, prefer pipeFormat
  if (columnSettings.usePipeFormat && columnSettings.pipeFormat) {
    return {
      format: columnSettings.pipeFormat,
      pipeFormat: columnSettings.pipeFormat,
    };
  }

  // If pipeFormat exists, use it
  if (columnSettings.pipeFormat) {
    return {
      format: columnSettings.pipeFormat,
      pipeFormat: columnSettings.pipeFormat,
    };
  }

  if (columnSettings.format) {
    return {
      format: columnSettings.format,
      pipeFormat: columnSettings.format,
    };
  }

  // Return empty strings if no format specified
  return {
    format: '',
    pipeFormat: '',
  };
}

/**
 * Converts ColumnSettings type to ColumnType/ColumnDataType
 */
function mapColumnType(type: 'text' | 'numeric' | 'boolean' | 'date'): { columnType: ColumnType; dataType: ColumnDataType } {
  switch (type) {
    case 'numeric':
      return { columnType: 'number', dataType: 'number' };
    case 'boolean':
      return { columnType: 'boolean', dataType: 'boolean' };
    case 'date':
      return { columnType: 'date', dataType: 'date' };
    case 'text':
    default:
      return { columnType: 'text', dataType: 'text' };
  }
}


/**
 * Converts a single ColumnSettings object to GridColumnData
 */
function convertColumnSettingsToGridColumnData(
    columnSettings: ColumnSettings,
    level = 1,
    parentField?: string,
): GridColumnData {
    const { columnType, dataType } = mapColumnType(columnSettings.type || 'text');
    const { format, pipeFormat } = resolveColumnFormat(columnSettings);

    // Convert child columns recursively
    const children = columnSettings.hasSubColumns && columnSettings.subColumns?.length 
        ? columnSettings.subColumns.map(subCol => 
            convertColumnSettingsToGridColumnData(subCol, level + 1, columnSettings.field),
        )
        : [];

    const gridColumnData = new GridColumnData({
        field: columnSettings.field,
        header: columnSettings.title || columnSettings.prettyField || columnSettings.field,
        exportHeader: columnSettings.title || columnSettings.prettyField || columnSettings.field,
        type: columnType,
        dataType: dataType,
        display: !columnSettings.hidden,
        filterable: columnSettings.filterable,
        exportable: columnSettings.exportable,
        frozen: columnSettings.sticky,
        format: format, // Angular pipe format
        pipeFormat: pipeFormat, // Angular pipe format
        width: columnSettings.width ? `${columnSettings.width}px` : '200px',
        prettyField: columnSettings.prettyField,
        fullField: columnSettings.fullField,
    
        // Nested column properties
        children: children,
        level: level,
        parent: parentField,
        colspan: children.length > 0 ? children.length : 1,
        rowspan: children.length > 0 ? 1 : 2, // Headers with children span 1 row, leaf columns span 2 rows
    
        // Template handling
        template: columnSettings.template as any,
        displayFunction: columnSettings.templateFn ? 
            (value: any, rowData: any) => columnSettings.templateFn!(rowData) : undefined,
        
        // Export strategy based on available functions/templates
        exportStrategy: columnSettings.exportable === false ? 'skip' :
                    columnSettings.templateFn ? 'function' :
                    columnSettings.template ? 'template' : 'raw',
        
        exportFunction: columnSettings.templateFn ? 
            (value: any, rowData: any) => columnSettings.templateFn!(rowData) : undefined,
  });

  return gridColumnData;
}

/**
 * Converts a single ColumnSettings object to GridColumnData
 */
export function convertColumnSettings(columnSettings: ColumnSettings): GridColumnData {
  return convertColumnSettingsToGridColumnData(columnSettings);
}

/**
 * Converts an array of ColumnSettings objects to an array of GridColumnData objects
 */
export function convertColumnSettingsList(columnSettingsList: ColumnSettings[]): GridColumnData[] {
  return columnSettingsList.map(columnSettings => 
    convertColumnSettingsToGridColumnData(columnSettings),
  );
}

/**
 * Utility function to flatten nested GridColumnData for display purposes
 */
export function flattenGridColumns(columns: GridColumnData[]): GridColumnData[] {
  const flattened: GridColumnData[] = [];
  
  function flatten(cols: GridColumnData[], currentLevel = 1) {
    for (const col of cols) {
      flattened.push(col);
      if (col.children && col.children.length > 0) {
        flatten(col.children as GridColumnData[], currentLevel + 1);
      }
    }
  }
  
  flatten(columns);
  return flattened;
}

/**
 * Utility function to calculate proper colspan and rowspan values for nested headers
 */
export function calculateColumnSpans(columns: GridColumnData[]): GridColumnData[] {
  function calculateSpans(cols: GridColumnData[], maxLevel = 1): GridColumnData[] {
    return cols.map(col => {
      if (col.children && col.children.length > 0) {
        // Calculate colspan as total number of leaf columns
        const leafCount = countLeafColumns(col);
        col.colspan = leafCount;
        col.rowspan = 1;
        
        // Recursively calculate for children
        col.children = calculateSpans(col.children as GridColumnData[], maxLevel);
      } else {
        // Leaf columns span 1 column and remaining rows
        col.colspan = 1;
        col.rowspan = maxLevel - (col.level || 1) + 1;
      }
      return col;
    });
  }
  
  // First, find the maximum nesting level
  const maxLevel = findMaxLevel(columns);
  
  return calculateSpans(columns, maxLevel);
}


/**
 * Helper function to count leaf columns in a nested structure
 */
function countLeafColumns(column: GridColumnData): number {
  if (!column.children || column.children.length === 0) {
    return 1;
  }
  
  return column.children.reduce((count, child) => 
    count + countLeafColumns(child as GridColumnData), 0,
  );
}

/**
 * Helper function to find the maximum nesting level
 */
function findMaxLevel(columns: GridColumnData[]): number {
  let maxLevel = 1;
  
  function traverse(cols: GridColumnData[], currentLevel: number) {
    maxLevel = Math.max(maxLevel, currentLevel);
    for (const col of cols) {
      if (col.children && col.children.length > 0) {
        traverse(col.children as GridColumnData[], currentLevel + 1);
      }
    }
  }
  
  traverse(columns, 1);
  return maxLevel;
}

/**
 * Example usage and type-safe conversion with validation
 */
export function convertWithValidation( input: ColumnSettings | ColumnSettings[] ): GridColumnData | GridColumnData[] {
  if (Array.isArray(input)) {
    return convertColumnSettingsList(input);
  } else {
    return convertColumnSettings(input);
  }
}
